import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './SearchCrud.module.css';
import { Base_Url } from '../config';

const SearchCrud = () => {
    const [searchTerms, setSearchTerms] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Form state - removed priority since it's not in your backend
    const [formData, setFormData] = useState({
        term: '',
        path: '',
        is_external: false
    });
    
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);

    // Fetch all search terms
    const fetchSearchTerms = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${Base_Url}/terms`);
            setSearchTerms(response.data.terms);
        } catch (err) {
            setError('Failed to fetch search terms');
            console.error('Error fetching search terms:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSearchTerms();
    }, []);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            term: '',
            path: '',
            is_external: false
        });
        setEditingId(null);
        setShowForm(false);
        setError('');
        setSuccess('');
    };

    // Create new search term
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            await axios.post(`${Base_Url}/terms`, formData);
            setSuccess('Search term created successfully!');
            resetForm();
            fetchSearchTerms();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create search term');
        } finally {
            setLoading(false);
        }
    };

    // Update search term
    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            await axios.put(`${Base_Url}/terms/${editingId}`, formData);
            setSuccess('Search term updated successfully!');
            resetForm();
            fetchSearchTerms();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update search term');
        } finally {
            setLoading(false);
        }
    };

    // Delete search term
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this search term?')) {
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            await axios.delete(`${Base_Url}/terms/${id}`);
            setSuccess('Search term deleted successfully!');
            fetchSearchTerms();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete search term');
        } finally {
            setLoading(false);
        }
    };

    // Edit search term
    const handleEdit = (term) => {
        setFormData({
            term: term.term,
            path: term.path,
            is_external: term.is_external
        });
        setEditingId(term.id);
        setShowForm(true);
        setError('');
        setSuccess('');
    };

    // Submit form (create or update)
    const handleSubmit = editingId ? handleUpdate : handleCreate;

    return (
        <div className={styles.searchCrud}>
            <div className={styles.crudHeader}>
                <h2>Search Terms Management</h2>
                <button 
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    onClick={() => setShowForm(!showForm)}
                >
                    {showForm ? 'Cancel' : 'Add New Search Term'}
                </button>
            </div>

            {/* Success/Error Messages */}
            {error && (
                <div className={`${styles.alert} ${styles.alertError}`}>
                    {error}
                    <button onClick={() => setError('')} className={styles.closeBtn}>×</button>
                </div>
            )}
            {success && (
                <div className={`${styles.alert} ${styles.alertSuccess}`}>
                    {success}
                    <button onClick={() => setSuccess('')} className={styles.closeBtn}>×</button>
                </div>
            )}

            {/* Create/Edit Form */}
            {showForm && (
                <form onSubmit={handleSubmit} className={styles.searchForm}>
                    <h3>{editingId ? 'Edit' : 'Create'} Search Term</h3>
                    
                    <div className={styles.formGroup}>
                        <label htmlFor="term">Term *</label>
                        <input
                            type="text"
                            id="term"
                            name="term"
                            value={formData.term}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter search term"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="path">Path *</label>
                        <input
                            type="text"
                            id="path"
                            name="path"
                            value={formData.path}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter path/URL"
                        />
                    </div>

                    <div className={`${styles.formGroup} ${styles.checkboxGroup}`}>
                        <label>
                            <input
                                type="checkbox"
                                name="is_external"
                                checked={formData.is_external}
                                onChange={handleInputChange}
                            />
                            Is External Link
                        </label>
                    </div>

                    <div className={styles.formActions}>
                        <button 
                            type="submit" 
                            className={`${styles.btn} ${styles.btnPrimary}`}
                            disabled={loading}
                        >
                            {loading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                        </button>
                        <button 
                            type="button" 
                            className={`${styles.btn} ${styles.btnSecondary}`}
                            onClick={resetForm}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            )}

            {/* Search Terms List */}
            <div className={styles.searchTermsList}>
                <h3>Search Terms ({searchTerms.length})</h3>
                
                {loading && !searchTerms.length ? (
                    <div className={styles.loading}>Loading search terms...</div>
                ) : (
                    <div className={styles.tableContainer}>
                        <table className={styles.termsTable}>
                            <thead>
                                <tr>
                                    <th>Term</th>
                                    <th>Path</th>
                                    <th>Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {searchTerms.map(term => (
                                    <tr key={term.id}>
                                        <td className={styles.termCell}>
                                            <strong>{term.term}</strong>
                                        </td>
                                        <td style={{textTransform: 'lowercase'}} className={styles.pathCell}>
                                            <a 
                                                href={term.path} 
                                                target={term.is_external ? "_blank" : "_self"}
                                                rel="noopener noreferrer"
                                            >
                                                {term.path}
                                            </a>
                                        </td>
                                        <td>
                                            <span className={`${styles.badge} ${term.is_external ? styles.badgeExternal : styles.badgeInternal}`}>
                                                {term.is_external ? 'External' : 'Internal'}
                                            </span>
                                        </td>
                                        <td className={styles.actionsCell}>
                                            <button
                                                className={`${styles.btn} ${styles.btnEdit}`}
                                                onClick={() => handleEdit(term)}
                                                title="Edit"
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                className={`${styles.btn} ${styles.btnDelete}`}
                                                onClick={() => handleDelete(term.id)}
                                                title="Delete"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {searchTerms.length === 0 && !loading && (
                            <div className={styles.noData}>
                                No search terms found. Create your first search term!
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SearchCrud;